import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { stageFile, stageLink, itemProblem, recordingSuffixes } from './quickAddFiles';
import { postQuickAddItem } from './quickAddUpload';
import { ZONE } from './instructorHome';
import {
  EMPTY_PUBLISH_FIELDS, getPublishAtFromForm, publishTimingError,
} from '../../../pages/admin/content/addContentModal/useAddContentModal/publishTiming';

/**
 * Quick Add state: target course + week, the "Ready to post" list, when
 * students see it, the email toggle, and posting everything in one go.
 */
export default function useQuickAdd({ courses, defaultCourseId, onPosted }) {
  const [courseId, setCourseId] = useState(defaultCourseId || null);
  const [items, setItems] = useState([]);
  const [timing, setTiming] = useState(EMPTY_PUBLISH_FIELDS);
  const [sendEmail, setSendEmail] = useState(true);
  const [posting, setPosting] = useState(false);
  const [message, setMessage] = useState(null); // { tone: 'ok'|'error', text }
  const abortRef = useRef(null);

  // Follow the suggested course until the instructor picks one.
  const touchedRef = useRef(false);
  useEffect(() => {
    if (!touchedRef.current && defaultCourseId) setCourseId(defaultCourseId);
  }, [defaultCourseId]);

  const course = useMemo(() => (courses || []).find((c) => c.id === courseId) || null, [courses, courseId]);
  const [targetWeek, setTargetWeek] = useState(0);
  const effectiveWeek = targetWeek; // 0 = General (not tied to a week)

  const chooseCourse = useCallback((id) => {
    touchedRef.current = true;
    setCourseId(id);
    setTargetWeek(0);
  }, []);

  /** Changing the week up top moves every file still waiting with it. */
  const chooseWeek = useCallback((w) => {
    setTargetWeek(w);
    setItems((prev) => prev.map((i) => (i.status === 'ready' || i.status === 'error' ? { ...i, week: i.type === 'syllabus' ? 0 : w } : i)));
  }, []);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setMessage(null);
    setItems((prev) => [...prev.filter((i) => i.status !== 'done'), ...files.map((f) => stageFile(f, effectiveWeek))]);
  }, [effectiveWeek]);

  const addLink = useCallback(() => {
    setMessage(null);
    setItems((prev) => [...prev.filter((i) => i.status !== 'done'), stageLink(effectiveWeek)]);
  }, [effectiveWeek]);

  const updateItem = useCallback((key, patch) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch, ...(i.status === 'error' ? { status: 'ready', error: '' } : {}) } : i)));
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);

  const clear = useCallback(() => {
    if (posting) abortRef.current?.abort();
    setItems([]);
    setMessage(null);
  }, [posting]);

  const postable = items.filter((i) => i.status === 'ready' || i.status === 'error');

  const post = useCallback(async () => {
    if (!course || postable.length === 0 || posting) return;
    // "Tomorrow" is a preset date: it posts like any other scheduled time.
    const when = timing.publishMode === 'tomorrow' ? { ...timing, publishMode: 'later' } : timing;
    const timingErr = publishTimingError(when);
    if (timingErr) { setMessage({ tone: 'error', text: timingErr }); return; }

    // Check every row first, so a typo in one link doesn't leave the batch half posted.
    const problems = new Map(postable.map((i) => [i.key, itemProblem(i)]).filter(([, p]) => p));
    if (problems.size) {
      setItems((prev) => prev.map((i) => (problems.has(i.key) ? { ...i, status: 'error', error: problems.get(i.key) } : i)));
      setMessage({ tone: 'error', text: 'Nothing was posted. Fix the notes below, then post again.' });
      return;
    }

    const publishAt = getPublishAtFromForm(when);
    const today = DateTime.now().setZone(ZONE).startOf('day');
    const suffixes = recordingSuffixes(postable);
    const controller = new AbortController();
    abortRef.current = controller;
    setPosting(true);
    setMessage(null);

    let done = 0;
    let failed = 0;
    for (const item of postable) {
      if (controller.signal.aborted) break;
      setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, status: 'uploading', percent: 0, error: '' } : i)));
      try {
        await postQuickAddItem({
          course,
          item,
          recordingDate: today.toISODate(),
          recordingDateUtc: today.toUTC().toISO(),
          publishAt,
          sendEmail,
          suffix: suffixes.get(item.key),
          signal: controller.signal,
          onProgress: (p) => setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, percent: p?.percent || 0 } : i))),
        });
        done += 1;
        setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, status: 'done', percent: 100 } : i)));
      } catch (error) {
        failed += 1;
        setItems((prev) => prev.map((i) => (i.key === item.key ? { ...i, status: 'error', error: error.message || 'Upload failed' } : i)));
      }
    }

    abortRef.current = null;
    setPosting(false);
    if (done > 0) {
      setItems((prev) => prev.filter((i) => i.status !== 'done'));
      const where = effectiveWeek ? `${course.code} · Week ${effectiveWeek}` : `${course.code} · General`;
      const when = publishAt ? ' (scheduled)' : '';
      setMessage({
        tone: failed ? 'error' : 'ok',
        text: failed
          ? `Uploaded and posted ${done} file${done === 1 ? '' : 's'} to ${where}${when}. ${failed} didn’t go through — see below.`
          : `Successfully uploaded and ${publishAt ? 'scheduled' : 'posted'} ${done} file${done === 1 ? '' : 's'} to ${where}.`,
      });
      onPosted?.(course);
    } else if (failed) {
      setMessage({ tone: 'error', text: 'Nothing was posted. See the notes below.' });
    }
  }, [course, postable, posting, timing, sendEmail, effectiveWeek, onPosted]);

  return {
    course, courseId, chooseCourse,
    week: effectiveWeek, chooseWeek,
    items, addFiles, addLink, updateItem, removeItem, clear,
    timing, setTiming,
    sendEmail, setSendEmail,
    posting, post, postableCount: postable.length,
    message, setMessage,
  };
}
